import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Settings,
  FileText,
  Users,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle2,
  Truck,
  Package,
  Eye,
  EyeOff,
  Palette,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("orders");
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [shippingZones, setShippingZones] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  
  // Shipping zone modal
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    zone_name: "",
    postcodes: "",
    price: "",
    estimated_days: 3,
    active: true,
  });

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    short_description: "",
    category: "shelving_kit",
    price: "",
    sale_price: "",
    images: "",
    bracket_type: "",
    bracket_colour: "",
    shelf_dimensions: "",
    includes: "",
    stock_status: "in_stock",
    featured: false,
    published: false,
  });

  // Material modal
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    name: "",
    colour_hex: "#FFFFFF",
    price_per_sqm: "",
    active: true,
    sort_order: 0,
  });

  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [token, activeTab]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` },
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "orders") {
        const res = await axios.get(`${API}/orders`, getAuthHeaders());
        setOrders(res.data);
      } else if (activeTab === "pricing") {
        const res = await axios.get(`${API}/pricing`);
        setPricing(res.data);
      } else if (activeTab === "shipping") {
        const res = await axios.get(`${API}/shipping-zones`);
        setShippingZones(res.data);
      } else if (activeTab === "subscribers") {
        const res = await axios.get(`${API}/newsletter/subscribers`, getAuthHeaders());
        setSubscribers(res.data);
      } else if (activeTab === "products") {
        const res = await axios.get(`${API}/admin/products`, getAuthHeaders());
        setProducts(res.data);
      } else if (activeTab === "materials") {
        const res = await axios.get(`${API}/admin/materials`, getAuthHeaders());
        setMaterials(res.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const handleSavePricing = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/pricing`, pricing, getAuthHeaders());
      toast.success("Pricing updated");
    } catch (error) {
      toast.error("Failed to update pricing");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?new_status=${status}`, {}, getAuthHeaders());
      toast.success("Order status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Shipping zone handlers
  const handleSaveShippingZone = async (e) => {
    e.preventDefault();
    try {
      const postcodeArray = zoneForm.postcodes.split(",").map(p => p.trim().toUpperCase());
      const payload = {
        zone_name: zoneForm.zone_name,
        postcodes: postcodeArray,
        price: parseFloat(zoneForm.price),
        estimated_days: parseInt(zoneForm.estimated_days),
        active: zoneForm.active,
      };

      if (editingZone) {
        await axios.put(`${API}/shipping-zones/${editingZone.id}`, payload, getAuthHeaders());
        toast.success("Shipping zone updated");
      } else {
        await axios.post(`${API}/shipping-zones`, payload, getAuthHeaders());
        toast.success("Shipping zone added");
      }
      setShowShippingModal(false);
      setEditingZone(null);
      setZoneForm({ zone_name: "", postcodes: "", price: "", estimated_days: 3, active: true });
      fetchData();
    } catch (error) {
      toast.error("Failed to save shipping zone");
    }
  };

  const handleEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      zone_name: zone.zone_name,
      postcodes: zone.postcodes.join(", "),
      price: zone.price,
      estimated_days: zone.estimated_days,
      active: zone.active,
    });
    setShowShippingModal(true);
  };

  const handleDeleteZone = async (id) => {
    if (!window.confirm("Delete this shipping zone?")) return;
    try {
      await axios.delete(`${API}/shipping-zones/${id}`, getAuthHeaders());
      toast.success("Shipping zone deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete shipping zone");
    }
  };

  // Product handlers
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const imagesArray = productForm.images
        ? productForm.images.split("\n").filter(url => url.trim()).map(url => ({ url: url.trim(), alt: productForm.name }))
        : [];
      
      const includesArray = productForm.includes
        ? productForm.includes.split("\n").filter(item => item.trim())
        : [];

      const payload = {
        name: productForm.name,
        description: productForm.description,
        short_description: productForm.short_description,
        category: productForm.category,
        price: parseFloat(productForm.price),
        sale_price: productForm.sale_price ? parseFloat(productForm.sale_price) : null,
        images: imagesArray,
        bracket_type: productForm.bracket_type,
        bracket_colour: productForm.bracket_colour,
        shelf_dimensions: productForm.shelf_dimensions,
        includes: includesArray,
        stock_status: productForm.stock_status,
        featured: productForm.featured,
        published: productForm.published,
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload, getAuthHeaders());
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, getAuthHeaders());
        toast.success("Product created");
      }
      setShowProductModal(false);
      setEditingProduct(null);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: "",
      description: "",
      short_description: "",
      category: "shelving_kit",
      price: "",
      sale_price: "",
      images: "",
      bracket_type: "",
      bracket_colour: "",
      shelf_dimensions: "",
      includes: "",
      stock_status: "in_stock",
      featured: false,
      published: false,
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      short_description: product.short_description || "",
      category: product.category,
      price: product.price,
      sale_price: product.sale_price || "",
      images: product.images?.map(img => img.url).join("\n") || "",
      bracket_type: product.bracket_type || "",
      bracket_colour: product.bracket_colour || "",
      shelf_dimensions: product.shelf_dimensions || "",
      includes: product.includes?.join("\n") || "",
      stock_status: product.stock_status,
      featured: product.featured,
      published: product.published,
    });
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, getAuthHeaders());
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleTogglePublished = async (product) => {
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { published: !product.published }, getAuthHeaders());
      toast.success(product.published ? "Product unpublished" : "Product published");
      fetchData();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  // Material handlers
  const openMaterialModal = (material = null) => {
    if (material) {
      setEditingMaterial(material);
      setMaterialForm({
        name: material.name,
        colour_hex: material.colour_hex,
        image_url: material.image_url || "",
        price_per_sqm: material.price_per_sqm,
        active: material.active,
        sort_order: material.sort_order || 0,
      });
    } else {
      setEditingMaterial(null);
      setMaterialForm({ name: "", colour_hex: "#FFFFFF", image_url: "", price_per_sqm: "", active: true, sort_order: 0 });
    }
    setShowMaterialModal(true);
  };

  const handleMaterialImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setMaterialForm(p => ({ ...p, image_url: res.data.url }));
      toast.success("Image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    }
  };

  const handleSaveMaterial = async () => {
    if (!materialForm.name || !materialForm.price_per_sqm) {
      toast.error("Name and price are required");
      return;
    }
    try {
      const payload = { ...materialForm, price_per_sqm: parseFloat(materialForm.price_per_sqm), sort_order: parseInt(materialForm.sort_order) || 0, image_url: materialForm.image_url || null };
      if (editingMaterial) {
        await axios.put(`${API}/admin/materials/${editingMaterial.id}`, payload, getAuthHeaders());
        toast.success("Material updated");
      } else {
        await axios.post(`${API}/admin/materials`, payload, getAuthHeaders());
        toast.success("Material created");
      }
      setShowMaterialModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save material");
    }
  };

  const handleDeleteMaterial = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    try {
      await axios.delete(`${API}/admin/materials/${id}`, getAuthHeaders());
      toast.success("Material deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  const navItems = [
    { id: "orders", icon: <FileText className="w-5 h-5" />, label: "Orders" },
    { id: "products", icon: <Package className="w-5 h-5" />, label: "Products" },
    { id: "materials", icon: <Palette className="w-5 h-5" />, label: "Materials" },
    { id: "pricing", icon: <Settings className="w-5 h-5" />, label: "Pricing" },
    { id: "shipping", icon: <Truck className="w-5 h-5" />, label: "Shipping" },
    { id: "subscribers", icon: <Users className="w-5 h-5" />, label: "Newsletter" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex">
      {/* Sidebar */}
      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="p-6 border-b border-[#E7E5E4]">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">S</span>
            </div>
            <span className="font-semibold text-lg">Admin</span>
          </Link>
        </div>

        <nav className="py-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`admin-nav-item w-full ${activeTab === item.id ? "active" : ""}`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E7E5E4]">
          <button onClick={handleLogout} className="admin-nav-item w-full text-red-600 hover:bg-red-50" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8" data-testid="admin-main">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#0F3D2E]" />
          </div>
        ) : (
          <>
            {/* Orders Tab */}
            {activeTab === "orders" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="orders-tab">
                <div className="mb-8">
                  <h1 className="text-3xl font-medium">Orders</h1>
                  <p className="text-[#57534E]">Manage customer order requests</p>
                </div>

                <div className="stone-card p-0 overflow-hidden">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Customer</th>
                        <th>Details</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#57534E]">No orders yet</td></tr>
                      ) : (
                        orders.map((order) => (
                          <tr key={order.id}>
                            <td className="font-mono">{order.reference}</td>
                            <td>
                              <div>
                                <div className="font-medium">{order.customer_name}</div>
                                <div className="text-sm text-[#57534E]">{order.customer_email}</div>
                              </div>
                            </td>
                            <td>
                              {order.length ? `${order.length}mm x ${order.depth}mm x ${order.quantity}` : order.notes?.substring(0, 50)}
                            </td>
                            <td>£{order.total_price?.toFixed(2) || "N/A"}</td>
                            <td>
                              <span className={`px-2 py-1 text-xs uppercase ${order.status === "completed" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                {order.status}
                              </span>
                            </td>
                            <td>
                              {order.status === "order" && (
                                <Button size="sm" onClick={() => handleUpdateOrderStatus(order.id, "completed")} className="text-xs" data-testid={`complete-order-${order.id}`}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />Complete
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Products Tab */}
            {activeTab === "products" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="products-tab">
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <h1 className="text-3xl font-medium">Products</h1>
                    <p className="text-[#57534E]">Manage shelving kits and products</p>
                  </div>
                  <Button className="btn-primary" onClick={() => { setEditingProduct(null); resetProductForm(); setShowProductModal(true); }} data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />Add Product
                  </Button>
                </div>

                <div className="stone-card p-0 overflow-hidden">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#57534E]">No products yet. Add your first shelving kit!</td></tr>
                      ) : (
                        products.map((product) => (
                          <tr key={product.id}>
                            <td>
                              <div className="flex items-center gap-3">
                                {product.images?.[0] ? (
                                  <img src={product.images[0].url} alt="" className="w-12 h-12 object-cover" />
                                ) : (
                                  <div className="w-12 h-12 bg-[#F5F5F4] flex items-center justify-center"><Package className="w-6 h-6 text-[#D6D3D1]" /></div>
                                )}
                                <div>
                                  <div className="font-medium">{product.name}</div>
                                  {product.shelf_dimensions && <div className="text-sm text-[#57534E]">{product.shelf_dimensions}</div>}
                                </div>
                              </div>
                            </td>
                            <td className="capitalize">{product.category.replace('_', ' ')}</td>
                            <td>
                              {product.sale_price ? (
                                <div>
                                  <span className="font-medium text-[#0F3D2E]">£{product.sale_price.toFixed(2)}</span>
                                  <span className="text-sm text-[#57534E] line-through ml-2">£{product.price.toFixed(2)}</span>
                                </div>
                              ) : (
                                <span className="font-medium">£{product.price.toFixed(2)}</span>
                              )}
                            </td>
                            <td>
                              <span className={`px-2 py-1 text-xs uppercase ${
                                product.stock_status === 'in_stock' ? 'bg-green-100 text-green-800' :
                                product.stock_status === 'low_stock' ? 'bg-amber-100 text-amber-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {product.stock_status.replace('_', ' ')}
                              </span>
                            </td>
                            <td>
                              <button onClick={() => handleTogglePublished(product)} className={`flex items-center gap-1 text-sm ${product.published ? 'text-green-600' : 'text-[#57534E]'}`}>
                                {product.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                {product.published ? 'Published' : 'Draft'}
                              </button>
                            </td>
                            <td>
                              <div className="flex gap-2">
                                <button onClick={() => handleEditProduct(product)} className="p-2 hover:bg-[#F5F5F4] rounded" data-testid={`edit-product-${product.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteProduct(product.id)} className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`delete-product-${product.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Materials Tab */}
            {activeTab === "materials" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="materials-tab">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h1 className="text-3xl font-medium">Stone Colours</h1>
                    <p className="text-[#57534E]">Manage available stone colours and per-colour pricing</p>
                  </div>
                  <Button onClick={() => openMaterialModal()} className="bg-[#0F3D2E] hover:bg-[#0F3D2E]/90" data-testid="add-material-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Colour
                  </Button>
                </div>

                <div className="stone-card overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E7E5E4]">
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#57534E]">Colour</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#57534E]">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#57534E]">Price/m²</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#57534E]">Order</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#57534E]">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#57534E]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materials.length === 0 ? (
                        <tr><td colSpan={6} className="py-8 text-center text-[#57534E]">No materials yet. Add your first stone colour.</td></tr>
                      ) : (
                        materials.map((mat) => (
                          <tr key={mat.id} className="border-b border-[#E7E5E4] last:border-0">
                            <td className="py-3 px-4">
                              {mat.image_url ? (
                                <img src={`${process.env.REACT_APP_BACKEND_URL}${mat.image_url}`} alt={mat.name} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                              ) : (
                                <span className="w-8 h-8 rounded-full border border-gray-200 inline-block" style={{ backgroundColor: mat.colour_hex }} />
                              )}
                            </td>
                            <td className="py-3 px-4 font-medium">{mat.name}</td>
                            <td className="py-3 px-4">£{mat.price_per_sqm.toFixed(2)}</td>
                            <td className="py-3 px-4 text-[#57534E]">{mat.sort_order}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 text-xs rounded ${mat.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {mat.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <button onClick={() => openMaterialModal(mat)} className="p-2 hover:bg-[#F5F5F4] rounded" data-testid={`edit-material-${mat.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteMaterial(mat.id)} className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`delete-material-${mat.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Material Modal */}
                {showMaterialModal && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                      <h2 className="text-xl font-medium mb-4">{editingMaterial ? 'Edit' : 'Add'} Stone Colour</h2>
                      <div className="space-y-4">
                        <div>
                          <Label className="input-label">Name</Label>
                          <Input value={materialForm.name} onChange={(e) => setMaterialForm(p => ({ ...p, name: e.target.value }))} className="input-field" placeholder="e.g. Carrara White" data-testid="material-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="input-label">Colour Swatch</Label>
                            <div className="flex items-center gap-2">
                              <input type="color" value={materialForm.colour_hex} onChange={(e) => setMaterialForm(p => ({ ...p, colour_hex: e.target.value }))} className="w-10 h-10 border rounded cursor-pointer" data-testid="material-colour" />
                              <Input value={materialForm.colour_hex} onChange={(e) => setMaterialForm(p => ({ ...p, colour_hex: e.target.value }))} className="input-field flex-1" />
                            </div>
                          </div>
                          <div>
                            <Label className="input-label">Price per m² (£)</Label>
                            <Input type="number" step="0.01" value={materialForm.price_per_sqm} onChange={(e) => setMaterialForm(p => ({ ...p, price_per_sqm: e.target.value }))} className="input-field" placeholder="0.00" data-testid="material-price" />
                          </div>
                        </div>
                        <div>
                          <Label className="input-label">Swatch Image</Label>
                          <div className="flex items-center gap-3">
                            {materialForm.image_url && (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${materialForm.image_url}`} alt="Swatch" className="w-16 h-16 rounded object-cover border border-gray-200" />
                            )}
                            <input type="file" accept="image/*" onChange={handleMaterialImageUpload} className="text-sm" data-testid="material-image-upload" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="input-label">Sort Order</Label>
                            <Input type="number" value={materialForm.sort_order} onChange={(e) => setMaterialForm(p => ({ ...p, sort_order: e.target.value }))} className="input-field" />
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={materialForm.active} onChange={(e) => setMaterialForm(p => ({ ...p, active: e.target.checked }))} className="w-4 h-4 accent-[#0F3D2E]" />
                              <span className="text-sm">Active</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3 mt-6">
                        <Button variant="outline" onClick={() => setShowMaterialModal(false)}>Cancel</Button>
                        <Button onClick={handleSaveMaterial} className="bg-[#0F3D2E] hover:bg-[#0F3D2E]/90" data-testid="save-material-btn">
                          {editingMaterial ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Pricing Tab */}
            {activeTab === "pricing" && pricing && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="pricing-tab">
                <div className="mb-8">
                  <h1 className="text-3xl font-medium">Pricing Settings</h1>
                  <p className="text-[#57534E]">Configure rates and costs for custom quotes</p>
                </div>

                <form onSubmit={handleSavePricing} className="max-w-2xl">
                  <div className="stone-card space-y-6">
                    <h3 className="text-lg font-medium border-b border-[#E7E5E4] pb-4">Base Rates</h3>
                    <div>
                      <Label className="input-label">Quartz Rate (£/m²)</Label>
                      <Input type="number" step="0.01" value={pricing.quartz_rate_per_sqm} onChange={(e) => setPricing((prev) => ({ ...prev, quartz_rate_per_sqm: parseFloat(e.target.value) }))} className="input-field" />
                    </div>

                    <h3 className="text-lg font-medium border-b border-[#E7E5E4] pb-4 pt-4">Edge Polishing (£/linear metre)</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="input-label">Front Edge</Label>
                        <Input type="number" step="0.01" value={pricing.edge_polished_front} onChange={(e) => setPricing((prev) => ({ ...prev, edge_polished_front: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                      <div>
                        <Label className="input-label">Front + Sides</Label>
                        <Input type="number" step="0.01" value={pricing.edge_polished_front_sides} onChange={(e) => setPricing((prev) => ({ ...prev, edge_polished_front_sides: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                      <div>
                        <Label className="input-label">All Edges</Label>
                        <Input type="number" step="0.01" value={pricing.edge_all_polished} onChange={(e) => setPricing((prev) => ({ ...prev, edge_all_polished: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium border-b border-[#E7E5E4] pb-4 pt-4">Additions (£)</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="input-label">Drip Groove</Label>
                        <Input type="number" step="0.01" value={pricing.drip_groove} onChange={(e) => setPricing((prev) => ({ ...prev, drip_groove: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                      <div>
                        <Label className="input-label">Radius Corners</Label>
                        <Input type="number" step="0.01" value={pricing.radius_corners} onChange={(e) => setPricing((prev) => ({ ...prev, radius_corners: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                      <div>
                        <Label className="input-label">Cut-out</Label>
                        <Input type="number" step="0.01" value={pricing.cutout_price} onChange={(e) => setPricing((prev) => ({ ...prev, cutout_price: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium border-b border-[#E7E5E4] pb-4 pt-4">Local Delivery & Other</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="input-label">Local Delivery (£)</Label>
                        <Input type="number" step="0.01" value={pricing.local_delivery_price} onChange={(e) => setPricing((prev) => ({ ...prev, local_delivery_price: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                      <div>
                        <Label className="input-label">VAT Rate (%)</Label>
                        <Input type="number" step="0.01" value={pricing.vat_rate} onChange={(e) => setPricing((prev) => ({ ...prev, vat_rate: parseFloat(e.target.value) }))} className="input-field" />
                      </div>
                      <div>
                        <Label className="input-label">Lead Time (days)</Label>
                        <Input type="number" value={pricing.lead_time_days} onChange={(e) => setPricing((prev) => ({ ...prev, lead_time_days: parseInt(e.target.value) }))} className="input-field" />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium border-b border-[#E7E5E4] pb-4 pt-4">Product Extras (£)</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="input-label">Silicone</Label>
                        <Input type="number" step="0.01" value={pricing.silicone_price ?? 8.99} onChange={(e) => setPricing((prev) => ({ ...prev, silicone_price: parseFloat(e.target.value) }))} className="input-field" data-testid="pricing-silicone" />
                      </div>
                      <div>
                        <Label className="input-label">Wedges</Label>
                        <Input type="number" step="0.01" value={pricing.wedges_price ?? 4.99} onChange={(e) => setPricing((prev) => ({ ...prev, wedges_price: parseFloat(e.target.value) }))} className="input-field" data-testid="pricing-wedges" />
                      </div>
                      <div>
                        <Label className="input-label">Brackets</Label>
                        <Input type="number" step="0.01" value={pricing.brackets_price ?? 24.99} onChange={(e) => setPricing((prev) => ({ ...prev, brackets_price: parseFloat(e.target.value) }))} className="input-field" data-testid="pricing-brackets" />
                      </div>
                    </div>

                    <Button type="submit" className="btn-primary" data-testid="save-pricing-btn">Save Changes</Button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Shipping Tab */}
            {activeTab === "shipping" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="shipping-tab">
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <h1 className="text-3xl font-medium">Shipping Zones</h1>
                    <p className="text-[#57534E]">Configure delivery rates by postcode area</p>
                  </div>
                  <Button className="btn-primary" onClick={() => { setEditingZone(null); setZoneForm({ zone_name: "", postcodes: "", price: "", estimated_days: 3, active: true }); setShowShippingModal(true); }} data-testid="add-shipping-zone-btn">
                    <Plus className="w-4 h-4 mr-2" />Add Zone
                  </Button>
                </div>

                <div className="stone-card p-0 overflow-hidden">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Zone Name</th>
                        <th>Postcodes</th>
                        <th>Price</th>
                        <th>Est. Days</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shippingZones.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-[#57534E]">No shipping zones configured.</td></tr>
                      ) : (
                        shippingZones.map((zone) => (
                          <tr key={zone.id}>
                            <td className="font-medium">{zone.zone_name}</td>
                            <td>
                              <div className="flex flex-wrap gap-1">
                                {zone.postcodes.slice(0, 5).map((pc) => (
                                  <span key={pc} className="px-2 py-0.5 bg-[#F5F5F4] text-xs">{pc}</span>
                                ))}
                                {zone.postcodes.length > 5 && <span className="px-2 py-0.5 text-xs text-[#57534E]">+{zone.postcodes.length - 5} more</span>}
                              </div>
                            </td>
                            <td>£{zone.price.toFixed(2)}</td>
                            <td>{zone.estimated_days} days</td>
                            <td>
                              <span className={`px-2 py-1 text-xs uppercase ${zone.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                                {zone.active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <div className="flex gap-2">
                                <button onClick={() => handleEditZone(zone)} className="p-2 hover:bg-[#F5F5F4] rounded"><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => handleDeleteZone(zone.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Subscribers Tab */}
            {activeTab === "subscribers" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="subscribers-tab">
                <div className="mb-8">
                  <h1 className="text-3xl font-medium">Newsletter Subscribers</h1>
                  <p className="text-[#57534E]">{subscribers.length} active subscribers</p>
                </div>

                <div className="stone-card p-0 overflow-hidden">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Subscribed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.length === 0 ? (
                        <tr><td colSpan={2} className="text-center py-8 text-[#57534E]">No subscribers yet</td></tr>
                      ) : (
                        subscribers.map((sub) => (
                          <tr key={sub.id}>
                            <td>{sub.email}</td>
                            <td className="text-[#57534E]">{new Date(sub.subscribed_at).toLocaleDateString()}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </>
        )}
      </main>

      {/* Shipping Zone Modal */}
      <Dialog open={showShippingModal} onOpenChange={setShowShippingModal}>
        <DialogContent data-testid="shipping-zone-modal">
          <DialogHeader>
            <DialogTitle>{editingZone ? "Edit Shipping Zone" : "Add Shipping Zone"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveShippingZone} className="space-y-4">
            <div>
              <Label className="input-label">Zone Name</Label>
              <Input required value={zoneForm.zone_name} onChange={(e) => setZoneForm((prev) => ({ ...prev, zone_name: e.target.value }))} className="input-field" placeholder="e.g. South West England" />
            </div>
            <div>
              <Label className="input-label">Postcode Prefixes (comma separated)</Label>
              <Input required value={zoneForm.postcodes} onChange={(e) => setZoneForm((prev) => ({ ...prev, postcodes: e.target.value }))} className="input-field" placeholder="e.g. BS, BA, GL, EX" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="input-label">Price (£)</Label>
                <Input type="number" step="0.01" required value={zoneForm.price} onChange={(e) => setZoneForm((prev) => ({ ...prev, price: e.target.value }))} className="input-field" />
              </div>
              <div>
                <Label className="input-label">Estimated Days</Label>
                <Input type="number" min="1" required value={zoneForm.estimated_days} onChange={(e) => setZoneForm((prev) => ({ ...prev, estimated_days: e.target.value }))} className="input-field" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox id="zone-active" checked={zoneForm.active} onCheckedChange={(checked) => setZoneForm((prev) => ({ ...prev, active: checked }))} />
              <Label htmlFor="zone-active" className="text-sm cursor-pointer">Zone is active</Label>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="btn-secondary flex-1" onClick={() => setShowShippingModal(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary flex-1">{editingZone ? "Update" : "Add"} Zone</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveProduct} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="input-label">Product Name</Label>
                <Input required value={productForm.name} onChange={(e) => setProductForm((prev) => ({ ...prev, name: e.target.value }))} className="input-field" placeholder="e.g. Marble White Shelf Kit - 600mm" data-testid="product-name" />
              </div>
              <div className="col-span-2">
                <Label className="input-label">Short Description</Label>
                <Input value={productForm.short_description} onChange={(e) => setProductForm((prev) => ({ ...prev, short_description: e.target.value }))} className="input-field" placeholder="Brief description for listings" />
              </div>
              <div className="col-span-2">
                <Label className="input-label">Full Description</Label>
                <Textarea required value={productForm.description} onChange={(e) => setProductForm((prev) => ({ ...prev, description: e.target.value }))} className="input-field min-h-[100px]" placeholder="Detailed product description..." data-testid="product-description" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="input-label">Category</Label>
                <Select value={productForm.category} onValueChange={(value) => setProductForm((prev) => ({ ...prev, category: value }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shelving_kit">Shelving Kit</SelectItem>
                    <SelectItem value="chopping_board">Chopping Board</SelectItem>
                    <SelectItem value="hot_pan_stand">Hot Pan Stand</SelectItem>
                    <SelectItem value="accessory">Accessory</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="input-label">Shelf Dimensions</Label>
                <Input value={productForm.shelf_dimensions} onChange={(e) => setProductForm((prev) => ({ ...prev, shelf_dimensions: e.target.value }))} className="input-field" placeholder="e.g. 600mm x 200mm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="input-label">Price (£)</Label>
                <Input type="number" step="0.01" required value={productForm.price} onChange={(e) => setProductForm((prev) => ({ ...prev, price: e.target.value }))} className="input-field" data-testid="product-price" />
              </div>
              <div>
                <Label className="input-label">Sale Price (£) - Optional</Label>
                <Input type="number" step="0.01" value={productForm.sale_price} onChange={(e) => setProductForm((prev) => ({ ...prev, sale_price: e.target.value }))} className="input-field" placeholder="Leave empty if no sale" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="input-label">Bracket Type</Label>
                <Input value={productForm.bracket_type} onChange={(e) => setProductForm((prev) => ({ ...prev, bracket_type: e.target.value }))} className="input-field" placeholder="e.g. Hidden, L-Bracket" />
              </div>
              <div>
                <Label className="input-label">Bracket Colour</Label>
                <Input value={productForm.bracket_colour} onChange={(e) => setProductForm((prev) => ({ ...prev, bracket_colour: e.target.value }))} className="input-field" placeholder="e.g. Black, Chrome" />
              </div>
            </div>

            <div>
              <Label className="input-label">Image URLs (one per line)</Label>
              <Textarea value={productForm.images} onChange={(e) => setProductForm((prev) => ({ ...prev, images: e.target.value }))} className="input-field min-h-[80px]" placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg" data-testid="product-images" />
            </div>

            <div>
              <Label className="input-label">Kit Includes (one item per line)</Label>
              <Textarea value={productForm.includes} onChange={(e) => setProductForm((prev) => ({ ...prev, includes: e.target.value }))} className="input-field min-h-[80px]" placeholder="1x Quartz shelf&#10;2x Hidden brackets&#10;Fixing kit" />
            </div>

            <div>
              <Label className="input-label">Stock Status</Label>
              <Select value={productForm.stock_status} onValueChange={(value) => setProductForm((prev) => ({ ...prev, stock_status: value }))}>
                <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_stock">In Stock</SelectItem>
                  <SelectItem value="low_stock">Low Stock</SelectItem>
                  <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Checkbox id="product-featured" checked={productForm.featured} onCheckedChange={(checked) => setProductForm((prev) => ({ ...prev, featured: checked }))} />
                <Label htmlFor="product-featured" className="text-sm cursor-pointer">Featured Product</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="product-published" checked={productForm.published} onCheckedChange={(checked) => setProductForm((prev) => ({ ...prev, published: checked }))} />
                <Label htmlFor="product-published" className="text-sm cursor-pointer">Published (visible in shop)</Label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="btn-secondary flex-1" onClick={() => setShowProductModal(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-product-btn">{editingProduct ? "Update" : "Create"} Product</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
